import { useState, useEffect } from "react";
import useAPI from "../hooks/useAPI";

export default function useAdminFormStats(props) {
  const localUser = JSON.parse(localStorage.getItem("kdmUser"));
  const superUser = localUser.sub === process.env.REACT_APP_ADMIN_UID;

  // États pour les filtres de période
  const [periodType, setPeriodType] = useState("30days");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: null,
    endDate: null,
  });

  // États pour les statistiques
  const [stats, setStats] = useState({
    total: 0,
    byDate: [],
    byUser: [],
    trend: 0,
    averagePerDay: 0,
  });

  // Calculer les dates selon le type de période
  const calculateDates = (type) => {
    const end = new Date();
    let start = new Date();

    switch (type) {
      case "7days":
        start.setDate(end.getDate() - 7);
        break;
      case "30days":
        start.setDate(end.getDate() - 30);
        break;
      case "3months":
        start.setMonth(end.getMonth() - 3);
        break;
      case "year":
        start.setFullYear(end.getFullYear() - 1);
        break;
      case "custom":
        return { start: startDate, end: endDate };
      default:
        start.setDate(end.getDate() - 30);
    }

    return { start, end };
  };

  const { data, query: fetchStats } = useAPI({
    type: "get",
    url: "forms",
  });

  // Méthodes
  const handleApplyFilters = () => {
    if (periodType === "custom" && startDate && endDate) {
      setAppliedFilters({ startDate, endDate });
    } else {
      const dates = calculateDates(periodType);
      setAppliedFilters({ startDate: dates.start, endDate: dates.end });
    }
  };

  const handlePeriodTypeChange = (type) => {
    setPeriodType(type);
    if (type !== "custom") {
      const dates = calculateDates(type);
      setStartDate(dates.start);
      setEndDate(dates.end);
    } else {
      // En mode custom, s'assurer que les dates sont définies
      if (!startDate || !endDate) {
        const dates = calculateDates("30days");
        setStartDate(dates.start);
        setEndDate(dates.end);
      }
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleRefreshData = () => {
    fetchStats();
  };

  // Calculer les statistiques à partir des données reçues
  const calculateStats = (forms) => {
    if (!forms || !Array.isArray(forms)) {
      return {
        total: 0,
        byDate: [],
        byUser: [],
        trend: 0,
        averagePerDay: 0,
      };
    }

    // Filtrer les formulaires selon la période sélectionnée
    let filteredForms = forms;
    if (appliedFilters.startDate && appliedFilters.endDate) {
      // Créer des copies des dates pour ne pas modifier les originales
      const startTime = new Date(appliedFilters.startDate);
      startTime.setHours(0, 0, 0, 0); // Début de la journée

      const endTime = new Date(appliedFilters.endDate);
      endTime.setHours(23, 59, 59, 999); // Fin de la journée

      filteredForms = forms.filter((form) => {
        if (!form.created) return false;
        const formTime = new Date(form.created);
        return formTime >= startTime && formTime <= endTime;
      });
    }

    const total = filteredForms.length;

    // Grouper par date
    const byDateMap = filteredForms.reduce((acc, form) => {
      const date = new Date(form.created).toISOString().split("T")[0];
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const byDate = Object.entries(byDateMap)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Grouper par utilisateur
    const byUserMap = filteredForms.reduce((acc, form) => {
      const userName =
        `${form.usrFirstname || ""} ${form.usrLastname || ""}`.trim() ||
        "Anonyme";
      const userId = form.usrId || "unknown";
      const key = `${userId}_${userName}`;

      if (!acc[key]) {
        acc[key] = { userId, name: userName, count: 0 };
      }
      acc[key].count += 1;
      return acc;
    }, {});

    const byUser = Object.values(byUserMap).sort((a, b) => b.count - a.count);

    // Calculer la moyenne par jour
    const days =
      appliedFilters.startDate && appliedFilters.endDate
        ? Math.ceil(
            (appliedFilters.endDate - appliedFilters.startDate) /
              (1000 * 60 * 60 * 24)
          )
        : 30;
    const averagePerDay = days > 0 ? (total / days).toFixed(1) : 0;

    // Calculer la tendance
    const midPoint = Math.floor(byDate.length / 2);
    const firstHalf = byDate.slice(0, midPoint);
    const secondHalf = byDate.slice(midPoint);

    const firstHalfTotal = firstHalf.reduce((sum, item) => sum + item.count, 0);
    const secondHalfTotal = secondHalf.reduce(
      (sum, item) => sum + item.count,
      0
    );

    const trend =
      firstHalfTotal > 0
        ? (((secondHalfTotal - firstHalfTotal) / firstHalfTotal) * 100).toFixed(
            1
          )
        : 0;

    return {
      total,
      byDate,
      byUser,
      trend: parseFloat(trend),
      averagePerDay: parseFloat(averagePerDay),
    };
  };

  useEffect(() => {
    const dates = calculateDates("30days");
    setStartDate(dates.start);
    setEndDate(dates.end);
    setAppliedFilters({ startDate: dates.start, endDate: dates.end });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log("🔄 useEffect formulaires - data:", data);
    console.log("🔄 Filtres appliqués:", appliedFilters);

    if (data) {
      // Essayer différentes structures possibles
      let formsData = null;
      if (data.data) {
        console.log("✅ Trouvé data.data:", data.data.length, "formulaires");
        formsData = data.data;
      } else if (Array.isArray(data)) {
        console.log(
          "✅ data est un tableau direct:",
          data.length,
          "formulaires"
        );
        formsData = data;
      } else if (data.forms) {
        console.log("✅ Trouvé data.forms:", data.forms.length, "formulaires");
        formsData = data.forms;
      } else {
        console.log(
          "❌ Structure inconnue - Clés disponibles:",
          Object.keys(data)
        );
      }

      if (formsData) {
        const calculatedStats = calculateStats(formsData);
        console.log("📊 Stats formulaires calculées:", calculatedStats);
        setStats(calculatedStats);
      }
    } else {
      console.log("❌ Pas de données formulaires disponibles");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, appliedFilters]);

  return {
    stats,
    superUser,
    periodType,
    startDate,
    endDate,
    appliedFilters,
    onPeriodTypeChange: handlePeriodTypeChange,
    onStartDateChange: handleStartDateChange,
    onEndDateChange: handleEndDateChange,
    onApplyFilters: handleApplyFilters,
    onRefreshData: handleRefreshData,
  };
}
